import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SuggestionCommitteeMember, User } from '@masar/common/models';
import { TableResult } from '@masar/common/misc/table';
import { environment } from '@masar/env/environment';
import { miscFunctions, Result } from 'mnm-webapp';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface SuggestionCommitteeAccess {
    canManageCommittee: boolean;
    canViewCommitteeEvaluation: boolean;
    isCommitteeMember: boolean;
}

export interface SuggestionCommitteeCriterionOption {
    id: string;
    name: string;
    description?: string;
    weight?: number | null;
    groupName?: string;
    groupWeight?: number | null;
    groupOrder?: number;
    order?: number;
}

export interface SuggestionCommitteeCriterionAssignment {
    memberId: string;
    evaluationId?: string | null;
    evaluationName?: string;
    selectedCriterionIds: string[];
    criteria: SuggestionCommitteeCriterionOption[];
}

@Injectable({ providedIn: 'root' })
export class SuggestionCommitteeService {
    public constructor(private readonly httpClient: HttpClient) {}

    public getAccess(): Observable<SuggestionCommitteeAccess> {
        return this.httpClient
            .get<Result<SuggestionCommitteeAccess>>(
                `${environment.apiUrl}/suggestion-committee-member/access`
            )
            .pipe(map(result => result.extra));
    }

    public listMembers(
        keyword: string,
        pageNumber: number,
        pageSize: number
    ): Observable<TableResult<SuggestionCommitteeMember>> {
        return this.httpClient
            .get<Result<TableResult<SuggestionCommitteeMember>>>(
                `${environment.apiUrl}/suggestion-committee-member`,
                {
                    params: new HttpParams()
                        .append('keyword', keyword || '')
                        .append('pageNumber', `${pageNumber}`)
                        .append('pageSize', `${pageSize}`),
                }
            )
            .pipe(map(result => result.extra));
    }

    public getOtherUsers(
        keyword: string,
        pageNumber: number,
        pageSize: number
    ): Observable<TableResult<User>> {
        return this.httpClient
            .get<Result<TableResult<User>>>(
                `${environment.apiUrl}/suggestion-committee-member/other`,
                {
                    params: new HttpParams()
                        .append('keyword', keyword || '')
                        .append('pageNumber', `${pageNumber}`)
                        .append('pageSize', `${pageSize}`),
                }
            )
            .pipe(map(result => result.extra));
    }

    public addMember(member: {
        user: { id: string; name?: string };
        role: string;
    }): Observable<SuggestionCommitteeMember> {
        return this.httpClient
            .post<Result<SuggestionCommitteeMember>>(
                `${environment.apiUrl}/suggestion-committee-member`,
                miscFunctions.objectToURLParams({
                    member: JSON.stringify(member),
                })
            )
            .pipe(map(result => result.extra));
    }

    public updateMember(member: {
        id: string;
        role: string;
    }): Observable<SuggestionCommitteeMember> {
        return this.httpClient
            .put<Result<SuggestionCommitteeMember>>(
                `${environment.apiUrl}/suggestion-committee-member`,
                miscFunctions.objectToURLParams({
                    member: JSON.stringify(member),
                })
            )
            .pipe(map(result => result.extra));
    }

    public removeMember(id: string): Observable<string> {
        return this.httpClient
            .delete<Result<unknown>>(
                `${environment.apiUrl}/suggestion-committee-member?id=${id}`
            )
            .pipe(map(result => result.messages[0]));
    }

    public getCriterionAssignment(
        memberId: string
    ): Observable<SuggestionCommitteeCriterionAssignment> {
        return this.httpClient
            .get<Result<SuggestionCommitteeCriterionAssignment>>(
                `${environment.apiUrl}/suggestion-committee-member/${memberId}/criterion-assignment`
            )
            .pipe(map(result => result.extra));
    }

    public updateCriterionAssignment(
        memberId: string,
        criterionIds: string[]
    ): Observable<SuggestionCommitteeCriterionAssignment> {
        return this.httpClient
            .put<Result<SuggestionCommitteeCriterionAssignment>>(
                `${environment.apiUrl}/suggestion-committee-member/${memberId}/criterion-assignment`,
                miscFunctions.objectToURLParams({
                    command: JSON.stringify({
                        criterionIds,
                    }),
                })
            )
            .pipe(map(result => result.extra));
    }
}
